import { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { isEqual } from 'lodash';

import { getDoctorById, updateDoctor } from 'api/doctors';
import { avatarUpload } from 'api/request';

import ViewComponent from 'pages/doctor/edit/view_component';

import { ReactComponent as ArrowRightSVG } from 'icons/arrow-right.svg';
import { INTELLIO_SPECIALTIES, TYPES } from 'config/doctor';
import { COUNTRIES } from 'config/country';

const tariffsMapping = [
  'Standard teleconsultations',
  'Emergency evening teleconsultations',
  'Saturday teleconsultations',
  'Sunday / holiday teleconsultations',
];

const DoctorsEdit = () => {
  const [loading, setLoading] = useState(false);
  const [doctor, setDoctor] = useState({});
  const [validation, setValidation] = useState({
    firstName: true,
    lastName: true,
    email: true,
    type: true,
  });
  const [standardTC, setStandardTC] = useState({});
  const [eveningTC, setEveningTC] = useState({});
  const [saturdayTC, setSaturdayTC] = useState({});
  const [sundayHolidayTC, setSundayHolidayTC] = useState({});
  const [initialTariffs, setInitialTariffs] = useState({
    standardTC: {},
    eveningTC: {},
    saturdayTC: {},
    sundayHolidayTC: {},
  });

  const history = useHistory();
  const { id } = useParams();

  useEffect(() => {
    const fetchDoctor = async () => {
      const fetchedDoctor = await getDoctorById(id);
      delete fetchedDoctor.teleconsultations;

      const mappedDoctor = {
        ...fetchedDoctor,
        specialty: INTELLIO_SPECIALTIES.find(
          (s) => s.value === fetchedDoctor.specialty,
        ),
        country: COUNTRIES.find((c) => c.value === fetchedDoctor.country),
        type: TYPES.find((t) => t.value === fetchedDoctor.type),
      };

      const tariffs = tariffsMapping.map(
        (name) =>
          fetchedDoctor.tariffs?.find(
            (t) => t.status === 'active' && t.name === name,
          ) || {},
      );

      setDoctor(mappedDoctor);
      setStandardTC(tariffs[0]);
      setEveningTC(tariffs[1]);
      setSaturdayTC(tariffs[2]);
      setSundayHolidayTC(tariffs[3]);
      setInitialTariffs({
        standardTC: tariffs[0],
        eveningTC: tariffs[1],
        saturdayTC: tariffs[2],
        sundayHolidayTC: tariffs[3],
      });
    };

    fetchDoctor();
  }, [id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setDoctor((prevDoctor) => ({ ...prevDoctor, [name]: value }));
  };

  const onTariffChange = (e) => {
    const { name, value } = e.target;

    const updateFunction = {
      standardTC: setStandardTC,
      eveningTC: setEveningTC,
      saturdayTC: setSaturdayTC,
      sundayHolidayTC: setSundayHolidayTC,
    }[name];

    updateFunction((prev) => ({ ...prev, amount: value || 0 }));
  };

  const onKeyDown = (e) => {
    if ([69, 187, 189].includes(e.keyCode)) e.preventDefault();
  };

  const onSelectChange = (field, selectedOption) => {
    setDoctor((prevDoctor) => ({ ...prevDoctor, [field]: selectedOption }));
  };

  const onFileUpload = async (file) => {
    setLoading(true);

    const result = await avatarUpload('s3/upload', file.file);
    setDoctor((prevDoctor) => ({
      ...prevDoctor,
      avatarUrl: result.previewUrl,
    }));
    setLoading(false);
  };

  const checkValidity = () => {
    const newValidation = {
      firstName: !!doctor.firstName,
      lastName: !!doctor.lastName,
      email: !!doctor.email,
      type: !!doctor.type,
    };
    setValidation(newValidation);
    return Object.values(newValidation).every(Boolean);
  };

  const onSubmit = async () => {
    setLoading(true);

    if (checkValidity()) {
      let diff = [];

      const currentTariffs = {
        standardTC,
        eveningTC,
        saturdayTC,
        sundayHolidayTC,
      };

      Object.entries(currentTariffs).forEach(([key, value]) => {
        if (!isEqual(value, initialTariffs[key])) diff.push(value);
      });

      await updateDoctor(id, {
        ...doctor,
        specialty: doctor.specialty?.value,
        country: doctor.country?.value,
        type: doctor.type?.value,
        tariffs: diff,
      });

      setLoading(false);
      history.push(`/doctors/${id}`);
    } else {
      setLoading(false);
    }
  };
  return (
    <main className="th-main">
      <section className="th-canvas">
        <header className="th-canvas-header post-header">
          <h2 className="th-canvas-title">
            <span className="pointer" onClick={() => history.goBack()}>
              Go Back
            </span>
            <span>
              <ArrowRightSVG />
            </span>
            {doctor.lastName?.toUpperCase()} {doctor.firstName}
          </h2>

          <section className="view-actions">
            <button
              className="btn-green"
              data-cy="submit"
              onClick={() => onSubmit()}
            >
              <span>Save</span>
            </button>
          </section>
        </header>
        <ViewComponent
          doctor={doctor}
          standardTC={standardTC}
          eveningTC={eveningTC}
          saturdayTC={saturdayTC}
          sundayHolidayTC={sundayHolidayTC}
          onChange={onChange}
          onSelectChange={onSelectChange}
          onTariffChange={onTariffChange}
          onFileUpload={onFileUpload}
          onKeyDown={onKeyDown}
          loading={loading}
          validation={validation}
        />
      </section>
    </main>
  );
};

export default DoctorsEdit;
